import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { isDesktopBrowser } from './constants/deviceDetect';
import ReactDOM from 'react-dom';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    pressingFetch, authenticatedFetch, getPresserSession, clearPressingSession,
} from './AuthComponent';
import { useAuth } from './AuthComponent';
import { useStore } from './StoreContext';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { playSuccess, playError, preloadSounds } from './sounds';
import { useIdleLock } from './hooks';
import PressingOrderBoard from './PressingOrderBoard';
import { useT } from './i18n/I18nContext';
import { LocationPickerModal } from './PickingPortal';
import PortalParticipantsFooter from './PortalParticipantsFooter';
import NotificationBroadcastModal from './NotificationBroadcastModal';
import PortalDailySummary from './PortalDailySummary';

import { hasRole } from './constants/roles';
const IDLE_LOCK_MS = 15 * 60 * 1000;

// ── Camera Scanner ─────────────────────────────────────────────────────────────

function CameraScanner({ onDetect, onClose }) {
    const t = useT();
    const onDetectRef = useRef(onDetect);
    const onCloseRef  = useRef(onClose);
    onDetectRef.current = onDetect;
    onCloseRef.current  = onClose;

    useEffect(() => {
        const scannerId = 'pp-qr-scanner';
        const scanner = new Html5Qrcode(scannerId, {
            formatsToSupport: [
                Html5QrcodeSupportedFormats.QR_CODE,
                Html5QrcodeSupportedFormats.CODE_128,
                Html5QrcodeSupportedFormats.CODE_39,
                Html5QrcodeSupportedFormats.EAN_13,
                Html5QrcodeSupportedFormats.UPC_A,
            ],
        });
        let stopped = false;

        const stopScanner = () => {
            if (stopped) return Promise.resolve();
            stopped = true;
            return scanner.stop().catch(() => {});
        };

        scanner.start(
            { facingMode: 'environment' },
            { fps: 15, qrbox: { width: 300, height: 120 } },
            (text) => {
                stopScanner().finally(() => onDetectRef.current(text));
            },
            () => { /* frame with no barcode — ignore */ }
        ).catch(() => {
            onCloseRef.current();
        });

        return () => { stopScanner(); };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="pkp-camera-overlay">
            <div className="pkp-camera-header">
                <button onClick={onClose} className="pkp-camera-close">✕ {t('Close')}</button>
                <span className="pkp-camera-title">{t('Scan Barcode')}</span>
                <span style={{ width: 60 }} />
            </div>
            <div id="pp-qr-scanner" className="pkp-camera-video" />
        </div>
    );
}

// ── Order search ──────────────────────────────────────────────────────────────

function OrderSearch({ shopifyLocationId, fetchFn, presserSession, assignments, locationMap, onSelect }) {
    const t = useT();
    const [q, setQ]               = useState('');
    const [results, setResults]   = useState([]);
    const [loading, setLoading]   = useState(false);
    const [searched, setSearched] = useState(false);
    const [cameraOpen, setCameraOpen] = useState(false);
    const inputRef                = useRef(null);
    const debounceRef             = useRef(null);
    const autoSelectRef           = useRef(false);

    useEffect(() => {
        preloadSounds();
        return () => clearTimeout(debounceRef.current);
    }, []);

    // Keep search input focused for Bluetooth scanner support.
    // Re-focus shortly after blur so tapping elsewhere doesn't steal focus permanently.
    // Also explicitly focus on mount — the `autoFocus` attribute alone misses
    // the case where OrderSearch remounts (returning from a selected order or
    // a modal); ensures the cursor is always parked in the search box when
    // this view is on screen.
    useEffect(() => {
        const el = inputRef.current;
        if (!el) return;
        const focusOnce = () => { try { el.focus(); } catch {} };
        focusOnce();
        const handleBlur = () => { setTimeout(focusOnce, 50); };
        el.addEventListener('blur', handleBlur);
        return () => el.removeEventListener('blur', handleBlur);
    }, []);

    const search = useCallback((query) => {
        if (query.trim().length < 2) { setResults([]); setSearched(false); return Promise.resolve([]); }
        setLoading(true);

        // Admin sidebar mode (no presser session): single-location legacy path
        if (!presserSession || !assignments || assignments.length === 0) {
            return fetchFn(`/api/pressing/orders?shopify_location_id=${encodeURIComponent(shopifyLocationId)}&q=${encodeURIComponent(query)}`)
                .then(r => r.json())
                .then(data => { setResults(data); setSearched(true); return data; })
                .catch(() => { setResults([]); return []; })
                .finally(() => setLoading(false));
        }

        // Multi-location presser: group assignments by store_key and fan out
        const byStore = {};
        for (const a of assignments) {
            if (!byStore[a.store_key]) byStore[a.store_key] = [];
            byStore[a.store_key].push(a.shopify_location_id);
        }
        const storeKeys = Object.keys(byStore);
        const requests = storeKeys.map(sk => {
            const csv = byStore[sk].join(',');
            return fetchFn(
                `/api/pressing/orders?shopify_location_ids=${encodeURIComponent(csv)}&q=${encodeURIComponent(query)}`,
                undefined,
                sk,
            )
                .then(r => r.ok ? r.json() : [])
                .then(list => (Array.isArray(list) ? list : []).map(o => {
                    const locInfo = locationMap.get(String(o.shopify_location_id)) || {};
                    return {
                        ...o,
                        __store_key:     sk,
                        __location_name: locInfo.location_name || '',
                    };
                }))
                .catch(() => []);
        });
        return Promise.all(requests)
            .then(lists => {
                const merged = lists.flat();
                setResults(merged);
                setSearched(true);
                return merged;
            })
            .catch(() => { setResults([]); return []; })
            .finally(() => setLoading(false));
    }, [shopifyLocationId, fetchFn, presserSession, assignments, locationMap]);

    const handleChange = (e) => {
        const v = e.target.value;
        setQ(v);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => search(v), 400);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            clearTimeout(debounceRef.current);
            autoSelectRef.current = true; // auto-select if exactly 1 result (covers Bluetooth scanner)
            search(q);
        }
    };

    const buildCtx = (order) => {
        if (!presserSession || !assignments || assignments.length === 0) return null;
        return {
            orderShopifyLocationId: order.shopify_location_id || assignments[0]?.shopify_location_id || '',
            orderStoreKey:          order.__store_key || assignments[0]?.store_key || null,
        };
    };

    // Auto-select whenever a search narrows to exactly one order (typed, Enter, or scan).
    // Sounds only play for scan / Enter (autoSelectRef), matching Packing Portal's silent-type behavior.
    useEffect(() => {
        if (results.length === 1) {
            if (autoSelectRef.current) { autoSelectRef.current = false; playSuccess(); }
            onSelect(results[0].order_id, buildCtx(results[0]));
        } else if (autoSelectRef.current) {
            autoSelectRef.current = false;
            playError();
        }
    }, [results]); // eslint-disable-line react-hooks/exhaustive-deps

    const showBadge = (presserSession && assignments && assignments.length > 1);

    return (
        <div className="os-wrapper">
            {cameraOpen && (
                <CameraScanner
                    onDetect={(barcode) => {
                        autoSelectRef.current = true;
                        setCameraOpen(false);
                        setQ(barcode);
                        search(barcode);
                    }}
                    onClose={() => setCameraOpen(false)}
                />
            )}
            <div className="os-input-wrap">
                <input
                    ref={inputRef}
                    value={q}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    placeholder={t('Search by customer name or order number…')}
                    autoFocus
                    className="os-input"
                    onFocus={e => e.target.style.borderColor = '#1a4fa0'}
                    onBlur={e => e.target.style.borderColor = '#c9cccf'}
                />
                <button
                    className="pkp-camera-btn"
                    onClick={() => setCameraOpen(true)}
                    title={t('Scan order barcode')}
                >
                    📷
                </button>
                <span className="os-search-icon">🔍</span>
            </div>

            {loading && <p className="os-status">{t('Searching…')}</p>}

            {!loading && searched && results.length === 0 && (
                <p className="os-status">{t('No unfulfilled orders found for "{q}"', { q })}</p>
            )}

            {results.map(order => (
                <div
                    key={order.order_id}
                    onClick={() => onSelect(order.order_id, buildCtx(order))}
                    className="os-result"
                    onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'; e.currentTarget.style.borderColor = '#1a4fa0'; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = '#e5e7eb'; }}
                >
                    <div>
                        <div className="os-result__name">
                            {order.customer_name || '—'}
                            {showBadge && order.__location_name && (
                                <span className="pkp-order-badge">{order.__location_name}</span>
                            )}
                        </div>
                        <div className="os-result__meta">
                            {order.order_name} · {t(order.items_count === 1 ? '{n} item' : '{n} items', { n: order.items_count })}
                        </div>
                    </div>
                    <div className="os-result__arrow">›</div>
                </div>
            ))}
        </div>
    );
}


// ── Main PressingPortal component ─────────────────────────────────────────────

export default function PressingPortal({ fullscreen = false }) {
    const t = useT();
    const { user, emulating, startEmulation }       = useAuth();
    const { activeStore }           = useStore();
    const { orderId: urlOrderId }   = useParams();
    const navigate                  = useNavigate();
    const location                  = useLocation();
    const returnTo                  = location.state?.returnTo || null;
    const [selectedOrderId, setSelectedOrderId] = useState(urlOrderId || null);
    const [selectedOrderContext, setSelectedOrderContext] = useState(null); // { orderShopifyLocationId, orderStoreKey }
    const [bootstrapError, setBootstrapError] = useState('');
    const [presserSession]          = useState(() => getPresserSession());
    // Location picker: pending order awaiting location choice
    const [pendingLocOrder, setPendingLocOrder] = useState(null); // { id, ctx (partial) }
    const [pendingLocCandidates, setPendingLocCandidates] = useState([]);
    const [pendingNotifications, setPendingNotifications] = useState([]);
    // 2026-09-02: the order's broadcast notifications, kept for the
    // "Review notification" button in the Working-at banner.
    const [orderNotifications, setOrderNotifications] = useState([]);
    // 2026-05-19: session-start and session-end are now written via two
    // independent POSTs (session-open / session-close), not deferred to close
    // time. The ref tracks the most recently opened order so pagehide /
    // idle-lock / sign-out paths can still fire a close.
    const sessionOpenForRef = useRef(null);  // { orderId, storeKey, sloc }

    // Multi-location assignments (PIN-only session)
    const assignments = useMemo(() => presserSession?.assignments || [], [presserSession]);
    const locationMap = useMemo(() => {
        const m = new Map();
        for (const a of assignments) {
            m.set(String(a.shopify_location_id), { store_key: a.store_key, location_name: a.location_name });
        }
        return m;
    }, [assignments]);

    // Legacy (admin/emulating) path falls back here
    const legacyShopifyLocationId = emulating?.shopify_location_id || user?.shopify_location_id || '';
    // Header location text
    let locationName = '';
    if (presserSession) {
        if (assignments.length === 1) locationName = assignments[0].location_name || '';
        else if (assignments.length > 1) locationName = t('Multiple locations ({n})', { n: assignments.length });
        else locationName = presserSession.location_name || '';
    } else {
        locationName = emulating?.name || user?.location_name || '';
    }
    const presserName       = presserSession
        ? `${presserSession.first_name} ${presserSession.last_name}`
        : user?.user;

    // Trap browser back button in fullscreen portal to avoid redirect loops.
    useEffect(() => {
        if (!fullscreen) return;
        window.history.pushState(null, '', window.location.href);
        const trap = () => window.history.pushState(null, '', window.location.href);
        window.addEventListener('popstate', trap);
        return () => window.removeEventListener('popstate', trap);
    }, [fullscreen]);

    // Idle lock: 15 min of inactivity in fullscreen portal → PIN screen.
    // Fire session-close for the currently-open order BEFORE clearing the
    // PIN session so the operator's work is properly bracketed.
    useIdleLock(fullscreen && !!presserSession, IDLE_LOCK_MS, () => {
        const cur = sessionOpenForRef.current;
        if (cur) {
            try { fireSessionClose(cur.orderId, cur.storeKey, cur.sloc); } catch {}
        }
        clearPressingSession();
        const kiosk = localStorage.getItem('ops_portal_token');
        if (kiosk) sessionStorage.setItem('pressing_admin_token', kiosk);
        window.location.href = '/pressing-portal/login';
    });

    // fetchFn signature: (url, opts, storeKeyOverride = null).
    // Default store (for admin sidebar + single-assignment sessions):
    //   presser single-assignment → that assignment's store_key
    //   admin sidebar              → activeStore
    const defaultStoreKey = presserSession
        ? (assignments[0]?.store_key || activeStore || 'soccerpost')
        : (activeStore || 'soccerpost');
    const fetchFn = useCallback((url, opts, storeKeyOverride = null) => {
        const base = presserSession ? pressingFetch : authenticatedFetch;
        const sk = storeKeyOverride || defaultStoreKey;
        const fullUrl = url.includes('?store=') || url.includes('&store=')
            ? url
            : `${url}${url.includes('?') ? '&' : '?'}store=${sk}`;
        return base(fullUrl, opts);
    }, [presserSession, defaultStoreKey]);

    // Fire a session-open for the order the operator is opening NOW. The
    // backend is idempotent — duplicate opens for the same (presser, order)
    // no-op.
    const fireSessionOpen = useCallback((orderId, storeKey, sloc) => {
        if (!orderId) return;
        try {
            fetchFn(`/api/pressing/orders/${orderId}/session-open`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    portal_userid:       presserSession?.presser_id || null,
                    portal_username:     presserSession
                        ? `${presserSession.first_name} ${presserSession.last_name}`
                        : (user?.user || ''),
                    shopify_location_id: sloc || '',
                }),
            }, storeKey).catch(() => {});
            sessionOpenForRef.current = { orderId, storeKey, sloc };
        } catch {}
    }, [fetchFn, presserSession, user]);

    // Fire a session-close for the prior order. `keepalive: true` so navigation
    // and sign-out paths still flush the request. Backend writes _session_end
    // at NOW; no-op if no open start exists for this (presser, order).
    const fireSessionClose = useCallback((prevOrderId, prevStoreKey, prevSloc) => {
        if (!prevOrderId) return;
        sessionOpenForRef.current = null;
        try {
            fetchFn(`/api/pressing/orders/${prevOrderId}/session-close`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    portal_userid:       presserSession?.presser_id || null,
                    portal_username:     presserSession
                        ? `${presserSession.first_name} ${presserSession.last_name}`
                        : (user?.user || ''),
                    shopify_location_id: prevSloc || selectedOrderContext?.orderShopifyLocationId || '',
                }),
                keepalive: true,
            }, prevStoreKey).catch(() => {});
        } catch {}
    }, [fetchFn, presserSession, user, selectedOrderContext]);

    // Admin-mode resolver: mirror PickingPortal.resolveAdminOrderLoc. Fetches
    // the order's cache-locations; if exactly one, silently auto-selects it and
    // mirrors it to the sidebar via startEmulation. If more than one, opens
    // the LocationPickerModal via pendingLocOrder/pendingLocCandidates (same
    // state the PIN-mode flow reuses). Returns the resolved ctx synchronously
    // when auto-selected, or null when the picker was opened.
    const resolveAdminOrderLoc = useCallback(async (orderId) => {
        const storeKey = activeStore || 'soccerpost';
        let locs = [];
        try {
            const r = await authenticatedFetch(`/api/orders/${orderId}/cache-locations?store=${storeKey}`);
            if (r.ok) {
                const j = await r.json();
                locs = (j.locations || []).filter(l => l && l.shopify_location_id);
            }
        } catch { /* fall through */ }
        const _commit = (loc) => {
            try {
                startEmulation?.({
                    id:                  loc.id ?? null,
                    name:                loc.name || loc.location_name || '',
                    shopify_location_id: loc.shopify_location_id,
                    is_franchise:        !!loc.is_franchise,
                    store_key:           storeKey,
                });
            } catch { /* non-fatal */ }
            return { orderShopifyLocationId: loc.shopify_location_id, orderStoreKey: storeKey };
        };
        const urlLoc = new URLSearchParams(location.search).get('loc') || '';
        if (urlLoc && urlLoc !== 'all') {
            const hit = locs.find(l => String(l.shopify_location_id) === String(urlLoc));
            if (hit) return _commit(hit);
        }
        if (legacyShopifyLocationId) {
            const hit = locs.find(l => String(l.shopify_location_id) === String(legacyShopifyLocationId));
            if (hit) return _commit(hit);
        }
        if (locs.length === 1) return _commit(locs[0]);
        if (locs.length > 1) {
            setPendingLocOrder({ id: orderId, baseCtx: { orderStoreKey: storeKey } });
            setPendingLocCandidates(locs.map(l => ({
                id:                  l.id ?? null,
                shopify_location_id: l.shopify_location_id,
                location_name:       l.name || l.location_name || l.shopify_location_id,
                is_franchise:        !!l.is_franchise,
                store_key:           storeKey,
            })));
            return null;
        }
        return { orderShopifyLocationId: legacyShopifyLocationId || '', orderStoreKey: storeKey };
    }, [activeStore, legacyShopifyLocationId, location.search, startEmulation]);

    const openOrderWithCtx = useCallback((id, effCtx) => {
        // If we're switching from another order, fire close on the prior one
        // first — the orphan sweep would catch it eventually, but firing now
        // bounds the session at the precise switch moment.
        const prior = sessionOpenForRef.current;
        if (prior && prior.orderId && prior.orderId !== id) {
            fireSessionClose(prior.orderId, prior.storeKey, prior.sloc);
        }
        setSelectedOrderId(id);
        setSelectedOrderContext(effCtx);
        fireSessionOpen(id, effCtx?.orderStoreKey || null, effCtx?.orderShopifyLocationId || '');
        // Include ?loc=<sid> on fullscreen so a bookmark / reload of the
        // resulting URL survives the multi-assignment bootstrap guard.
        const _sid = effCtx?.orderShopifyLocationId || '';
        const _qs = _sid ? `?loc=${encodeURIComponent(_sid)}` : '';
        navigate(fullscreen
            ? `/pressing-portal/active/orders/${id}${_qs}`
            : `/store/${activeStore}/pressing-portal/orders/${id}`);
    }, [fullscreen, activeStore, navigate, fireSessionOpen, fireSessionClose]);

    const handleSelect = async (id, ctx = null) => {
        // Admin sidebar (no presser session): resolve the order's actual loc
        // via cache-locations. Auto-select single-loc silently (mirroring
        // to sidebar via startEmulation); prompt on multi-loc. Never fall
        // back to the JWT-primary loc — that was the pre-fix bug that let
        // press events land with an empty shopify_location_id.
        let effCtx = ctx;
        if (!effCtx && !presserSession) {
            effCtx = await resolveAdminOrderLoc(id);
            if (effCtx == null) return; // loc picker now open; onPick commits
        }

        // Multi-location check: only show the picker when the order is ACTUALLY at
        // multiple of the presser's assigned locations. Without this filter the
        // picker fired any time the presser had >1 assignments (e.g. Eric across
        // every Soccer Post location), even when a given order lives at only ONE
        // of those locations — forcing a meaningless choice.
        if (presserSession && assignments.length > 1 && effCtx) {
            const orderStoreKey = effCtx.orderStoreKey;
            const storeCandidates = assignments.filter(a => a.store_key === orderStoreKey);
            if (storeCandidates.length > 1) {
                let cacheLocs = [];
                try {
                    const r = await fetchFn(`/api/orders/${id}/cache-locations`, undefined, orderStoreKey);
                    if (r.ok) {
                        const j = await r.json();
                        cacheLocs = (j.locations || []).map(l => String(l.shopify_location_id));
                    }
                } catch {}
                const orderHere = storeCandidates.filter(a => cacheLocs.includes(String(a.shopify_location_id)));
                if (orderHere.length > 1) {
                    setPendingLocOrder({ id, baseCtx: effCtx });
                    setPendingLocCandidates(orderHere);
                    return;
                }
                if (orderHere.length === 1) {
                    openOrderWithCtx(id, { ...effCtx, orderShopifyLocationId: orderHere[0].shopify_location_id });
                    return;
                }
                // 0 hits or fetch error → fall through to the search-result's sloc.
            }
        }

        openOrderWithCtx(id, effCtx);
    };

    const handleBack = () => {
        if (selectedOrderId) {
            fireSessionClose(selectedOrderId,
                selectedOrderContext?.orderStoreKey || null,
                selectedOrderContext?.orderShopifyLocationId || '');
        }
        if (returnTo) { navigate(returnTo, { replace: true }); return; }
        setSelectedOrderId(null);
        setOrderNotifications([]);
        setSelectedOrderContext(null);
        navigate(fullscreen ? '/pressing-portal/active' : `/store/${activeStore}/pressing-portal`, { replace: true });
    };

    // Bootstrap deep-link: set context for URL-loaded orders.
    // Multi-assignment sessions cannot pre-resolve the order's location/store, so we show
    // an error and require the user to re-select from search.
    useEffect(() => {
        if (!urlOrderId) return;
        if (!presserSession) {
            // Admin sidebar: resolve the order's actual loc via
            // resolveAdminOrderLoc — auto-select if 1, prompt if >1. Never
            // set an empty shopify_location_id (pre-fix bug that let press
            // events land loc-less).
            (async () => {
                const ctx = await resolveAdminOrderLoc(urlOrderId);
                if (ctx) {
                    setSelectedOrderContext(ctx);
                    fireSessionOpen(urlOrderId, ctx.orderStoreKey, ctx.orderShopifyLocationId);
                }
            })();
            return;
        }
        // Honor `?loc=<sid>` on multi-assignment so a bookmark / deep-link
        // that includes the location skips the "open from search" guard.
        const _locFromUrl = new URLSearchParams(location.search).get('loc') || '';
        if (_locFromUrl && assignments.length > 1) {
            const hit = assignments.find(a => String(a.shopify_location_id) === String(_locFromUrl));
            if (hit) {
                const ctx = {
                    orderShopifyLocationId: hit.shopify_location_id,
                    orderStoreKey:          hit.store_key,
                };
                setSelectedOrderContext(ctx);
                fireSessionOpen(urlOrderId, ctx.orderStoreKey, ctx.orderShopifyLocationId);
                return;
            }
        }
        if (assignments.length === 1) {
            const only = assignments[0];
            const ctx = {
                orderShopifyLocationId: only.shopify_location_id,
                orderStoreKey:          only.store_key,
            };
            setSelectedOrderContext(ctx);
            fireSessionOpen(urlOrderId, ctx.orderStoreKey, ctx.orderShopifyLocationId);
        } else if (assignments.length > 1) {
            setBootstrapError(t('Please open this order from the search page.'));
            setSelectedOrderId(null);
            setOrderNotifications([]);
        setOrderNotifications([]);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-fullscreen on launch — iPad/Android kiosk only. Desktop runs as a
    // normal tab (matches QC / Picking portal behavior).
    useEffect(() => {
        const isDesktop = isDesktopBrowser();   // 2026-10-14: iPadOS 27 no longer reports maxTouchPoints > 1 (constants/deviceDetect.js)
        if (isDesktop) return;
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
        if (!fullscreen || isStandalone) return;
        if (document.fullscreenElement) return;
        if (document.documentElement.requestFullscreen) {
            document.documentElement.requestFullscreen().catch(() => {});
        }
    }, [fullscreen]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSignOut = () => {
        if (selectedOrderId) {
            fireSessionClose(selectedOrderId,
                selectedOrderContext?.orderStoreKey || null,
                selectedOrderContext?.orderShopifyLocationId || '');
        }
        sessionStorage.removeItem('presser_session');
        window.location.href = '/pressing-portal/login';
    };

    // pagehide / visibilitychange='hidden' catch the iPad-swipe-app-away and
    // tab-close paths. `keepalive: true` on the close POST lets the browser
    // flush the request during teardown.
    useEffect(() => {
        const flushClose = () => {
            const cur = sessionOpenForRef.current;
            if (cur) fireSessionClose(cur.orderId, cur.storeKey, cur.sloc);
        };
        const onVisibility = () => {
            if (document.visibilityState === 'hidden') flushClose();
        };
        window.addEventListener('pagehide', flushClose);
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            window.removeEventListener('pagehide', flushClose);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [fireSessionClose]);

    // Admins can search all locations when no specific location is selected
    const isAdmin = hasRole(user, ['admin']);
    const canSearch = presserSession ? assignments.length > 0 : (!!legacyShopifyLocationId || isAdmin);

    // Derive "Working at" location name for the banner shown inside the order board.
    const orderLocationName = useMemo(() => {
        if (!selectedOrderContext) return null;
        if (!presserSession || assignments.length <= 1) return null;
        const locId = selectedOrderContext.orderShopifyLocationId;
        if (!locId) return null;
        const a = assignments.find(a => String(a.shopify_location_id) === String(locId));
        return a?.location_name || null;
    }, [selectedOrderContext, presserSession, assignments]);

    // fetchFn wrapper for order-level requests — adds X-Emulate-Location header.
    const orderFetchFn = useCallback((url, opts, storeKeyOverride = null) => {
        if (!selectedOrderContext?.orderShopifyLocationId) return fetchFn(url, opts, storeKeyOverride);
        const mergedOpts = {
            ...opts,
            headers: {
                ...(opts?.headers || {}),
                'X-Emulate-Location': String(selectedOrderContext.orderShopifyLocationId),
            },
        };
        return fetchFn(url, mergedOpts, storeKeyOverride);
    }, [fetchFn, selectedOrderContext]);

    const content = (
        <>
            <NotificationBroadcastModal
                notifications={pendingNotifications}
                onAcknowledge={() => setPendingNotifications([])}
            />
            {/* Location picker modal */}
            {pendingLocOrder && pendingLocCandidates.length > 1 && (
                <LocationPickerModal
                    candidates={pendingLocCandidates}
                    onPick={(loc) => {
                        const { id, baseCtx } = pendingLocOrder;
                        setPendingLocOrder(null);
                        setPendingLocCandidates([]);
                        // Admin-mode picker: mirror the chosen loc to the
                        // sidebar dropdown (matches PickingPortal behavior).
                        if (!presserSession) {
                            try {
                                startEmulation?.({
                                    id:                  loc.id ?? null,
                                    name:                loc.location_name || '',
                                    shopify_location_id: loc.shopify_location_id,
                                    is_franchise:        !!loc.is_franchise,
                                    store_key:           baseCtx.orderStoreKey || activeStore || 'soccerpost',
                                });
                            } catch { /* non-fatal */ }
                        }
                        openOrderWithCtx(id, {
                            ...baseCtx,
                            orderShopifyLocationId: loc.shopify_location_id,
                        });
                    }}
                    onCancel={() => { setPendingLocOrder(null); setPendingLocCandidates([]); }}
                />
            )}

            {/* Guard: non-admin with no location assigned */}
            {!canSearch && (
                <p className="pp-no-loc">
                    {t('No location assigned. Use "View as" in the admin bar to select a location, or sign in as a location user.')}
                </p>
            )}

            {bootstrapError && <p className="pp-no-loc">{bootstrapError}</p>}

            {canSearch && !selectedOrderId && (
                <>
                    <OrderSearch
                        shopifyLocationId={legacyShopifyLocationId}
                        fetchFn={fetchFn}
                        presserSession={presserSession}
                        assignments={assignments}
                        locationMap={locationMap}
                        onSelect={handleSelect}
                    />
                    <PortalDailySummary
                        portal="pressing"
                        presserSession={presserSession}
                        fetchFn={fetchFn}
                    />
                </>
            )}

            {canSearch && selectedOrderId && selectedOrderContext && (
                <>
                    {orderLocationName && (
                        <div className="working-at-banner">
                            <span className="working-at-banner__icon">📍</span>
                            {t('Working at:')} {orderLocationName}
                        </div>
                    )}
                    <PortalParticipantsFooter
                        orderId={selectedOrderId}
                        storeKey={selectedOrderContext.orderStoreKey}
                        fetchFn={fetchFn}
                        roles={['pickers']}
                    />
                    <PressingOrderBoard
                        notifications={orderNotifications}
                        onReviewNotifications={() => setPendingNotifications(orderNotifications)}
                        orderId={selectedOrderId}
                        orderShopifyLocationId={selectedOrderContext.orderShopifyLocationId}
                        orderStoreKey={selectedOrderContext.orderStoreKey}
                        fetchFn={orderFetchFn}
                        onBack={handleBack}
                        presserName={presserName}
                        onActiveNotifications={(notifs) => {
                            const list = Array.isArray(notifs) ? notifs : [];
                            setOrderNotifications(list);
                            if (list.length) setPendingNotifications(list);
                        }}
                    />
                </>
            )}
        </>
    );

    if (fullscreen) {
        return (
            <div className="pp-fullscreen">
                {/* Header banner only on order search, not on the pressing view */}
                {!selectedOrderId && (
                    <div className="pp-header">
                        <div className="pp-header__left">
                            <span className="pp-header__icon">👕</span>
                            <div>
                                <div className="pp-header__title">{t('Pressing Portal')}</div>
                                <div className="pp-header__sub">
                                    {locationName || t('No location')}
                                </div>
                                <div className="pp-header__sub">
                                    {presserName || ''}
                                </div>
                            </div>
                        </div>
                        <div className="pp-header__right">
                            {presserSession && (
                                <button onClick={handleSignOut} className="pp-header-btn pp-header-btn--sign-out">
                                    {t('↩ Sign Out')}
                                </button>
                            )}
                        </div>
                    </div>
                )}
                <div className="pp-content">{content}</div>
            </div>
        );
    }

    return (
        <div className="pp-page">
            <div className="pp-page-header">
                <div>
                    <h1 className="pp-page-title">{t('Pressing Portal')}</h1>
                    {locationName && <div className="pp-page-loc">{locationName}</div>}
                </div>
            </div>
            {content}
        </div>
    );
}
